import { HttpErrorResponse } from '@angular/common/http';
import { MeetingAttachment, MeetingFileAttachment } from 'lfx-pcc';
import { MEETING_ATTACHMENT_ACCEPTED_EXTENSIONS_SET, MEETING_ATTACHMENT_MAX_SIZE_BYTES, getMeetingDocumentIcon } from '@utils/constants';
import {
  AttachmentScope,
  AttachmentScopeView,
  DecoratedAttachment,
  DecoratedFileAttachment,
  DecoratedLinkAttachment,
  PendingFileUpload,
} from '@shared/interfaces/meeting-attachments.interface';
import { DateTime } from 'luxon';

export interface FileValidationResult {
  ok: true;
}

export interface FileValidationError {
  ok: false;
  reason: string;
}

export type FileValidationOutcome = FileValidationResult | FileValidationError;

export function isPendingFileUpload(attachment: MeetingAttachment | PendingFileUpload): attachment is PendingFileUpload {
  return 'status' in attachment;
}

export function getAttachmentFileIcon(attachment: MeetingFileAttachment | PendingFileUpload): string {
  const name = isPendingFileUpload(attachment) ? attachment.file.name : attachment.file_name || attachment.name;
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['pdf'].includes(ext)) return 'fa-regular fa-file-pdf';
  if (['doc', 'docx', 'rtf', 'odt', 'pages'].includes(ext)) return 'fa-regular fa-file-word';
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)) return 'fa-regular fa-file-excel';
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return 'fa-regular fa-file-powerpoint';
  if (['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg', 'odg'].includes(ext)) return 'fa-regular fa-file-image';
  return 'fa-regular fa-file';
}

export function getAttachmentFileName(attachment: MeetingFileAttachment): string {
  return attachment.file_name || attachment.name;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Validates a file against size, extension, and duplicate constraints before queuing or uploading.
export function validateAttachmentFile(file: File, existingAttachments: MeetingAttachment[], pendingFiles: PendingFileUpload[]): FileValidationOutcome {
  if (file.size > MEETING_ATTACHMENT_MAX_SIZE_BYTES) {
    return { ok: false, reason: `File "${file.name}" exceeds the ${formatFileSize(MEETING_ATTACHMENT_MAX_SIZE_BYTES)} size limit.` };
  }

  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  if (!MEETING_ATTACHMENT_ACCEPTED_EXTENSIONS_SET.has(ext)) {
    return { ok: false, reason: `File type "${ext}" is not supported.` };
  }

  const isDuplicate =
    existingAttachments.some((a) => a.type === 'file' && (a.file_name === file.name || a.name === file.name)) ||
    pendingFiles.some((p) => p.file.name === file.name);
  if (isDuplicate) {
    return { ok: false, reason: `"${file.name}" is already attached. Skipping duplicate.` };
  }

  return { ok: true };
}

// Thrown when the server reports a terminal processing failure for an uploaded file,
// so retry logic can distinguish it from transient readiness-poll failures (which
// should resume polling the existing record rather than re-uploading).
export class AttachmentProcessingError extends Error {}

// Maps an upload failure to a user-facing reason. An HttpErrorResponse with status 0
// means the connection failed before the server confirmed the transfer (network outage,
// CORS, a browser extension blocking the S3 PUT, or a client-side timeout); statuses
// >= 400 mean the server rejected the call — surface the backend-provided message when
// one exists (the codebase-wide convention), falling back to generic copy. A raw string
// body (e.g. S3's XML error from the presigned PUT, which uses responseType 'text') is
// never shown verbatim. Any other status (e.g. a 2xx response that failed to parse) is
// a response-processing failure. A plain Error (e.g. from pollUntilReady) already
// carries a descriptive message.
export function describeUploadError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 0) {
      return 'Upload blocked before reaching the server — check your connection or disable browser extensions (such as ad blockers) that block uploads.';
    }
    if (err.status >= 400) {
      // The BFF returns JSON { message } / { Message }; the S3 PUT returns XML text, so
      // this stays undefined there and falls through to the generic line.
      const backendMessage = err.error?.message ?? err.error?.Message;
      if (typeof backendMessage === 'string' && backendMessage.trim()) {
        // Surface the HTTP status alongside the backend message so server rejections
        // always carry it (per this PR's documented behavior), even with a friendly message.
        return `${backendMessage} (HTTP ${err.status})`;
      }
      return `The server rejected the upload (HTTP ${err.status}). Please try again or contact support.`;
    }
    return `The upload failed while processing the server response (HTTP ${err.status}). Please try again or contact support.`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Upload failed for an unknown reason. Please try again.';
}

// Generic so it preserves the element type (MeetingAttachment, DecoratedAttachment, etc.)
export function sortByNewest<T extends { created_at?: string }>(attachments: T[]): T[] {
  return [...attachments].sort((a, b) => (new Date(b.created_at ?? '').getTime() || 0) - (new Date(a.created_at ?? '').getTime() || 0));
}

// Pre-computes display fields once when data arrives so view computeds only
// add dynamic flags (isDeleting, isDownloading, isNew) without re-running
// formatting helpers on every signal change.
export function decorateAttachment(a: MeetingAttachment): DecoratedAttachment {
  const createdByDisplay = a.created_by?.name || a.created_by?.username || null;
  if (a.type === 'file') {
    return {
      ...a,
      displayIcon: getAttachmentFileIcon(a),
      displayName: getAttachmentFileName(a),
      displaySize: a.file_size ? formatFileSize(a.file_size) : null,
      createdByDisplay,
    } as DecoratedFileAttachment;
  }
  return {
    ...a,
    displayIcon: getMeetingDocumentIcon(a.category ?? ''),
    createdByDisplay,
  } as DecoratedLinkAttachment;
}

/** Extracts the hostname (minus www.) from a URL for use as a title suggestion. */
export function extractDomainTitle(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

// An occurrence ID is the occurrence start as a unix timestamp. The meeting service carries it in seconds; past-meeting
// IDs built from webhooks carry milliseconds. Normalised to milliseconds so the same occurrence compares equal either way.
function occurrenceIdToMs(id: string | null | undefined): number | null {
  const value = Number(id);
  if (!id || !Number.isFinite(value) || value <= 0) return null;
  return value >= 1e11 ? value : value * 1000;
}

export function isSameOccurrenceId(a: string | null | undefined, b: string | null | undefined): boolean {
  const aMs = occurrenceIdToMs(a);
  const bMs = occurrenceIdToMs(b);
  return aMs !== null && aMs === bMs;
}

/** An attachment with no occurrence_id applies to every occurrence of the meeting. */
export function getAttachmentScope(attachment: { occurrence_id?: string }): AttachmentScope {
  return attachment.occurrence_id ? 'occurrence' : 'series';
}

/**
 * The attachments an occurrence shows: the series-wide ones plus the ones scoped to it.
 * With no occurrence only series-wide attachments qualify, so another date's materials never show up undated.
 */
export function filterAttachmentsForOccurrence<T extends { occurrence_id?: string }>(attachments: T[], occurrenceId: string | null | undefined): T[] {
  return attachments.filter((a) => !a.occurrence_id || isSameOccurrenceId(a.occurrence_id, occurrenceId));
}

/** Formats an occurrence ID as a short local date and time, or null when it is not a usable timestamp. */
export function formatOccurrenceId(occurrenceId: string | null | undefined): string | null {
  const ms = occurrenceIdToMs(occurrenceId);
  if (ms === null) return null;
  // A finite but out-of-range id gives an invalid DateTime, which would otherwise format as the text "Invalid DateTime".
  const date = DateTime.fromMillis(ms);
  return date.isValid ? date.toLocaleString({ weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
}

/**
 * What the list says about an attachment's scope. When editing one occurrence, a scoped attachment is "This occurrence";
 * when editing the whole series it names the date, since the series list holds every date's attachments.
 */
export function describeAttachmentScope(attachment: { occurrence_id?: string }, managedOccurrenceId: string | null): AttachmentScopeView {
  const scope = getAttachmentScope(attachment);
  if (scope === 'series') return { scope, scopeLabel: 'All occurrences' };
  if (managedOccurrenceId && isSameOccurrenceId(attachment.occurrence_id, managedOccurrenceId)) return { scope, scopeLabel: 'This occurrence' };
  const date = formatOccurrenceId(attachment.occurrence_id);
  return { scope, scopeLabel: date ? `Only ${date}` : 'One occurrence' };
}
